/**
 * @fileoverview Status mappings API service and Zod schemas.
 * @module features/status-mappings/api/statusMappingsService
 */

import { z } from 'zod'
import apiClient from '@/lib/apiClient'

/**
 * Enum for business view types.
 */
export const ViewTypeEnum = z.enum(['SALES_ANALYTICS', 'CANCELLATION_ANALYTICS', 'AUDIT'])
export type ViewType = z.infer<typeof ViewTypeEnum>

/**
 * StatusMappingDTO schema based on OpenAPI.
 */
export const StatusMappingDTOSchema = z
  .object({
    id: z.number().int().nonnegative(),
    externalStatus: z.string(),
    statusName: z.string().nullable().optional(),
    statusNameForCustomer: z.string().optional(),
    statusColor: z.string().optional(),
    viewType: ViewTypeEnum.nullable(),
    lastSyncedAt: z.string().nullable().optional(),
    createdAt: z.string().nullable().optional(),
    updatedAt: z.string().nullable().optional(),
  })
  .passthrough()
export type StatusMappingDTO = z.infer<typeof StatusMappingDTOSchema>

/**
 * CreateStatusMappingDTO schema.
 */
export const CreateStatusMappingDTOSchema = z.object({
  externalStatus: z.string().min(1),
  viewType: ViewTypeEnum,
})
export type CreateStatusMappingDTO = z.infer<typeof CreateStatusMappingDTOSchema>

/**
 * UpdateStatusMappingDTO schema (both fields optional per spec flexibility).
 */
export const UpdateStatusMappingDTOSchema = z.object({
  externalStatus: z.string().min(1).optional(),
  viewType: ViewTypeEnum.optional(),
})
export type UpdateStatusMappingDTO = z.infer<typeof UpdateStatusMappingDTOSchema>

/**
 * Bulk import request/response.
 */
export const BulkStatusMappingDTOSchema = z.object({
  mappings: z.array(CreateStatusMappingDTOSchema).min(1),
  replaceExisting: z.boolean().optional(),
})
export type BulkStatusMappingDTO = z.infer<typeof BulkStatusMappingDTOSchema>

export const BulkImportResultDTOSchema = z.object({
  created: z.number().int().nonnegative().optional(),
  updated: z.number().int().nonnegative().optional(),
  skipped: z.number().int().nonnegative().optional(),
  errors: z.array(z.string()).optional(),
})
export type BulkImportResultDTO = z.infer<typeof BulkImportResultDTOSchema>

/**
 * API calls
 */
export async function listStatusMappings(view?: ViewType): Promise<StatusMappingDTO[]> {
  const res = await apiClient.get('/admin/status-mappings', { params: view ? { view } : undefined })
  return z.array(StatusMappingDTOSchema).parse(res.data)
}

export async function createStatusMapping(
  payload: CreateStatusMappingDTO
): Promise<StatusMappingDTO> {
  const body = CreateStatusMappingDTOSchema.parse(payload)
  const res = await apiClient.post('/admin/status-mappings', body)
  return StatusMappingDTOSchema.parse(res.data)
}

export async function updateStatusMapping(
  id: number,
  payload: UpdateStatusMappingDTO
): Promise<StatusMappingDTO> {
  const body = UpdateStatusMappingDTOSchema.parse(payload)
  const res = await apiClient.put(`/admin/status-mappings/${id}`, body)
  return StatusMappingDTOSchema.parse(res.data)
}

export async function deleteStatusMapping(id: number): Promise<void> {
  await apiClient.delete(`/admin/status-mappings/${id}`)
}

export async function bulkImportMappings(
  payload: BulkStatusMappingDTO
): Promise<BulkImportResultDTO> {
  const body = BulkStatusMappingDTOSchema.parse(payload)
  const res = await apiClient.post('/admin/status-mappings/bulk', body)
  return BulkImportResultDTOSchema.parse(res.data)
}
